// ProfileCompleteness Component
// Shows profile completion percentage with visual indicator

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaChevronRight } from 'react-icons/fa';
import styles from './ProfileCompleteness.module.css';

const ProfileCompleteness = ({ score, profileId, onEdit }) => {
  const navigate = useNavigate();
  const [showDetails, setShowDetails] = useState(false);

  // Calculate color based on score
  const getColor = () => {
    if (score >= 80) return '#10b981'; // green
    if (score >= 60) return '#3b82f6'; // blue
    if (score >= 40) return '#f59e0b'; // yellow
    return '#ef4444'; // red
  };

  // Get status message
  const getStatusMessage = () => {
    if (score >= 80) return 'Excellent Profile!';
    if (score >= 60) return 'Good Progress';
    if (score >= 40) return 'Keep Going';
    return 'Get Started';
  };

  // Calculate circumference for circular progress
  const radius = 45;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (score / 100) * circumference;

  const handleImproveClick = () => {
    if (onEdit) {
      // Use the same handler as Edit Profile button
      onEdit();
    } else {
      // Fallback to direct navigation
      navigate(`/player-profile/${profileId}/edit`);
    }
  };

  return (
    <div className={styles.container}>
      <div 
        className={styles.progressCircle}
        onClick={() => setShowDetails(!showDetails)}
        style={{ cursor: 'pointer' }}
      >
        <svg width="100" height="100">
          {/* Background circle */}
          <circle
            cx="50"
            cy="50"
            r={radius}
            stroke="var(--bg-tertiary)"
            strokeWidth="6"
            fill="none"
          />
          {/* Progress circle */}
          <circle
            cx="50"
            cy="50"
            r={radius}
            stroke={getColor()}
            strokeWidth="6"
            fill="none"
            strokeDasharray={circumference}
            strokeDashoffset={strokeDashoffset}
            strokeLinecap="round"
            style={{
              transition: 'stroke-dashoffset 0.5s ease-in-out',
              transform: 'rotate(-90deg)',
              transformOrigin: '50% 50%'
            }}
          />
        </svg>
        <div className={styles.scoreText}>
          <span className={styles.score}>{score}%</span>
          <span className={styles.label}>Complete</span>
        </div>
      </div>

      <div className={styles.info}>
        <h4 className={styles.status}>{getStatusMessage()}</h4>
        <p className={styles.message}>
          {score < 100 
            ? `Complete your profile to get ${100 - score}% more visibility`
            : 'Your profile is fully optimized!'}
        </p>
        
        {score < 100 && (
          <button 
            className={styles.improveButton}
            onClick={handleImproveClick}
          >
            Improve Profile <FaChevronRight />
          </button>
        )}
      </div>

      {/* Details Tooltip */}
      {showDetails && (
        <div className={styles.tooltip}>
          <h5>Profile Sections</h5>
          <ul>
            <li className={score >= 20 ? styles.completed : ''}>
              Basic Information
            </li>
            <li className={score >= 40 ? styles.completed : ''}>
              Athletic Profile
            </li>
            <li className={score >= 55 ? styles.completed : ''}>
              Skills & Abilities
            </li>
            <li className={score >= 70 ? styles.completed : ''}>
              Experience
            </li>
            <li className={score >= 80 ? styles.completed : ''}>
              Media Gallery
            </li>
            <li className={score >= 90 ? styles.completed : ''}>
              Awards & Achievements
            </li>
            <li className={score >= 100 ? styles.completed : ''}>
              Availability Status
            </li>
          </ul>
        </div>
      )}
    </div>
  );
};

export default ProfileCompleteness;
